<template>
    <!-- here is the table -->
    <v-card class="mb-4">
        <v-card v-if="!abrigedTableMode">
                <v-card-actions>
                    <v-btn class="ies-green" @click="saveData()"><v-icon>save</v-icon> Save</v-btn>
                    <v-btn class="yellow" @click="unselectDates()" title="Press 'Esc' to clear date selections" ><v-icon>clear</v-icon> Clear Date Selections</v-btn>
                    <v-btn class="ies-coral" @click="undoChanges()"><v-icon>undo</v-icon> Undo Changes</v-btn>
                    
                    <span class="ml-4"> 
                        <span v-if="isSaving">
                            <v-icon class="spin">sync</v-icon>
                        </span>

                        <span v-if="saveSuccess">
                            <v-icon color="green">done</v-icon>
                            <span>Changes saved successfully</span>
                        </span>

                        <span v-if="saveError" class="ies-coral--text">
                            <v-icon color="ies-coral">error</v-icon>
                            <span>Changes could not be saved: {{ errorMessage }}</span>
                        </span>
                    </span>

                </v-card-actions>
            </v-card>
            
        <v-flex xs12 v-if="!abrigedTableMode">
            Last updated at: {{ lastUpdated.date }} by {{ lastUpdated.user }}
        </v-flex>
        <v-container fluid>
            <v-layout row wrap>
                <v-flex xs12 class="position-relative overflow-y-auto table-container" style="max-height: 80vh;">
                        <table class="daily-planner">
                        <thead>                                                
                            <th class="header-cell"><strong>{{ getMonthOfYear(dateFrom.getMonth()) }} {{ dateFrom.getFullYear() }} </strong></th>
                            <template v-for="(day) in calendarDates">
                                <th v-bind:class="{
                                    'calendar-date': true, 
                                    'weekend': day.dayIndex == 5 || day.dayIndex == 6,
                                    }">
                                    <div v-bind:class="{
                                        'date-out-of-range': dateIsOutOfRange(day.date),
                                    }">
                                        <div class="date-text">{{ day.dayString }}</div>
                                        <div class="date-number">{{ day.dateOfMonth }}</div>
                                        <div class="date-text">{{ day.monthString }}</div>
                                    </div>
                                </th>
                            </template>
                        </thead>

                        <tbody v-if="responseDataReady">
                            <template v-for="(roomType) in orderedHotelRooms" v-if="selectedRoomTypeKeys.includes(roomType.hotelRoomKey) || selectedRoomTypeKeys.length == 0">
                                <!-- availability -->
                                <ies-hotel-planner-availability-row
                                    @openEditAvailabilityDates="handleOpenEditAvailabilityDates"
                                    @selectAvailabilityDate="handleSelectAvailabilityDate"

                                    :roomType="roomType"
                                    :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                    :datesWithinRange="datesWithinRange"
                                    :isDisabled="isDisabled"
                                    :selectedAvailabilityDates="selectedAvailabilityDates"
                                    :availabilityCellDisplay="availabilityCellDisplay"


                                    :availabilityDateMode.sync="availabilityDateMode"
                                    :editingAvailabilityDates.sync="editingAvailabilityDates"
                                    :selectedRoomType.sync="selectedRoomType"
			                    	:cellWidthPx="tableCellWidthPx"
                                    />

                                <template v-for="(rateType) in roomType.rateTypes" v-if="!abrigedTableMode && (selectedRateTypeKeys.includes(rateType.rateTypeKey) || selectedTableViewOptions['rateTypeOptions'].length == 0)">
                                    <!-- rate standard -->
                                    <ies-hotel-planner-rate-row
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="rateType.name"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'rate_standard'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
			                        	:cellWidthPx="tableCellWidthPx"
                                        />
                                    
                                    <!-- rate single -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showSingleRate'] && roomType.single_occupancy"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="'Single Rate'"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'rate_single'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
			                        	:cellWidthPx="tableCellWidthPx"
                                        />
                                    
                                    <!-- extra cot -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showExtraCotRate'] && !showUniversalRates && roomType.extraBaby"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="extraBabyText"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'rate_extra_baby'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
			                        	:cellWidthPx="tableCellWidthPx"
                                        />

                                    <!-- extra child -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showExtraChildBedRate'] && !showUniversalRates && roomType.extraChild"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="extraChildText"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'rate_extra_child'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
			                        	:cellWidthPx="tableCellWidthPx"
                                        />
                                        
                                    <!-- extra oldchild -->
                                        <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showExtraOldChildBedRate'] && !showUniversalRates && roomType.extraOldChild"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="extraOldChildText"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'rate_extra_oldchild'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
			                        	:cellWidthPx="tableCellWidthPx"
                                        />

                                    <!-- extra adult -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showExtraBedRate'] && !showUniversalRates && roomType.extraAdult"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="extraAdultText"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'rate_extra_adult'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
                                        :cellWidthPx="tableCellWidthPx"
                                        />

                                    <!-- minimum stay -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showMinimumStay']"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="'Minimum Stay'"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'min_stay'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
			                        	:cellWidthPx="tableCellWidthPx"
                                        />

                                    <!-- maximum stay -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showMaximumStay']"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="'Maximum Stay'"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'max_stay'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"

                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
			                        	:cellWidthPx="tableCellWidthPx"
                                        />

                                    <!-- no check in days -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showNoCheckInDays'] && rateType.is_checkinout_variable"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="'No Check In Days'"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'days_checkin'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
				                        :cellWidthPx="tableCellWidthPx"
                                        />

                                    <!-- no check out days -->
                                    <ies-hotel-planner-rate-row
                                        v-if="selectedTableViewOptions['showNoCheckOutDays'] && rateType.is_checkinout_variable"
                                        @openEditRateDates="handleOpenEditRateDates"
                                        @selectRateDate="handleSelectRateDate"
                                        @unselectDates="unselectDates"

                                        :rowHeader="'No Check Out Days'"
                                        :ratesPerDate="rateType.ratesPerDate"
                                        :ratesPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].rateTypes[rateType.rateTypeKey].ratesPerDate"
                                        :availabilityPerDateSaved="localResponseData.tableData.hotelRooms[roomType.hotelRoomKey].availabilityPerDate"
                                        :standardOccupancy="roomType.standard_occupancy"
                                        :isDisabled="isDisabled"
                                        :rateColumn="'days_checkout'"
                                        :datesWithinRange="datesWithinRange"
                                        :editingRateDates="editingRateDates"
                                        :selectedRateDates="selectedRateDates"
                                        
                                        :rateDateMode.sync="rateDateMode"
                                        :selectedRateColumn.sync="selectedRateColumn"
				                        :cellWidthPx="tableCellWidthPx"
                                        />
                                </template>
                            </template>
                        </tbody>

                        <!-- universal rates -->
                        <tfoot v-if="showUniversalRates && !abrigedTableMode" class="sticky-footer" style="position:sticky; bottom: -5px; z-index: 1; background-color: white;">
                            <tr style="background-color: #d0d0d0;" class="sticky-footer-header">
                                <td>
                                    Extra Beds / Guests for all room types
                                </td>

                                <template v-for="(date) in calendarDates">
                                    <td>
                                        &nbsp;
                                    </td>
                                </template>
                            </tr>

                            <!-- extra baby -->
                            <ies-hotel-planner-universal-rate-row
                                v-if="selectedTableViewOptions['showExtraCotRate']"
                                @openEditUniversalRates="handleOpenEditUniversalRates"
                                
                                :disabled="isDisabled"
                                :rowHeader="extraBabyText"
                                :rowValue="rateExtraBabyUniversal"
                                :rateColumn="'extra_baby_rate_amount'"
                                :datesWithinRange="datesWithinRange"
                                :calendarDates="calendarDates"
                                :savedRates="localTableData.universalRates"
                                :unsavedRates="localResponseData.tableData.universalRates"
                                :selectedRateColumn.sync="selectedRateColumn"
                                :openEditUniversalRates.sync="openEditUniversalRates"
                                />
                            
                            <!-- extra child -->
                            <ies-hotel-planner-universal-rate-row
                                v-if="selectedTableViewOptions['showExtraChildBedRate']"
                                @openEditUniversalRates="handleOpenEditUniversalRates"
                                
                                :disabled="isDisabled"
                                :rowHeader="extraChildText"
                                :rowValue="rateExtraChildUniversal"
                                :rateColumn="'extra_child_rate_amount'"
                                :datesWithinRange="datesWithinRange"
                                :calendarDates="calendarDates"
                                :savedRates="localTableData.universalRates"
                                :unsavedRates="localResponseData.tableData.universalRates"
                                :selectedRateColumn.sync="selectedRateColumn"
                                :openEditUniversalRates.sync="openEditUniversalRates"
                                />
                            
                            <!-- extra oldchild -->
                            <ies-hotel-planner-universal-rate-row
                                v-if="selectedTableViewOptions['showExtraOldChildBedRate']"
                                @openEditUniversalRates="handleOpenEditUniversalRates"
                                
                                :disabled="isDisabled"
                                :rowHeader="extraOldChildText"
                                :rowValue="rateExtraOldchildUniversal"
                                :rateColumn="'extra_oldchild_rate_amount'"
                                :datesWithinRange="datesWithinRange"
                                :calendarDates="calendarDates"
                                :savedRates="localTableData.universalRates"
                                :unsavedRates="localResponseData.tableData.universalRates"
                                :selectedRateColumn.sync="selectedRateColumn"
                                :openEditUniversalRates.sync="openEditUniversalRates"
                                />
                            
                            <!-- extra adult -->
                            <ies-hotel-planner-universal-rate-row
                                v-if="selectedTableViewOptions['showExtraBedRate']"
                                @openEditUniversalRates="handleOpenEditUniversalRates"
                                
                                :disabled="isDisabled"
                                :rowHeader="extraAdultText"
                                :rowValue="rateExtraAdultUniversal"
                                :rateColumn="'extra_adult_rate_amount'"
                                :datesWithinRange="datesWithinRange"
                                :calendarDates="calendarDates"
                                :savedRates="localTableData.universalRates"
                                :unsavedRates="localResponseData.tableData.universalRates"
                                :selectedRateColumn.sync="selectedRateColumn"
                                :openEditUniversalRates.sync="openEditUniversalRates"
                                />
                        </tfoot>
                    </table>
                </v-flex>
            </v-layout>

            
            <!-- edit form -->
            <ies-hotel-planner-edit-form
            v-if="showEditForm"
            
            @closeEditForm="handleCloseEditForm"
            @unselectDates="unselectDates"
            
            :selectedRoomType="selectedRoomType"
            :selectedRateColumn="selectedRateColumn"
            :currencyCode="currencycode"
            :selectedRateDates="selectedRateDates"
            :selectedAvailabilityDates="selectedAvailabilityDates"

            :openEditAvailabilityDates="openEditAvailabilityDates"
            :openEditRateDates="openEditRateDates"
            :openEditUniversalRates="openEditUniversalRates"
            
            :localTableData.sync="localTableData"
            />
        </v-container>
        <v-card v-if="!abrigedTableMode">
            <v-card-actions>
                <v-btn class="ies-green" @click="saveData()"><v-icon>save</v-icon> Save</v-btn>
                <v-btn class="yellow" @click="unselectDates()" title="Press 'Esc' to clear date selections" ><v-icon>clear</v-icon> Clear Date Selections</v-btn>
                <v-btn class="ies-coral" @click="undoChanges()"><v-icon>undo</v-icon> Undo Changes</v-btn>
                
                <span class="ml-4"> 
                    <span v-if="isSaving">
                        <v-icon class="spin">sync</v-icon>
                    </span>
    
                    <span v-if="saveSuccess">
                        <v-icon color="green">done</v-icon>
                        <span>Changes saved successfully</span>
                    </span>
    
                    <span v-if="saveError" class="ies-coral--text">
                        <v-icon color="ies-coral">error</v-icon>
                        <span>Changes could not be saved: {{ errorMessage }}</span>
                    </span>
                </span>
    
            </v-card-actions>
        </v-card>
    </v-card>
</template>

<script>
/**
* A Vue component dedicated to letting the user control availability and rates per
* hotel room for a given date range
*
* Props:  hotelkey
*/
import axios from 'axios';

export default {
    name: 'hotel-planner',
    props: ['hotelkey', 'currencycode', 'tableViewOptions', 'selectedTableViewOptions', 'dateFrom', 'isDisabled', 'abrigedTableMode', 'availabilityCellDisplay'],
    data () {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); 
        return {
            // Table data
            currentDate: currentDate,
            localResponseData: {},
            localTableData: [],
            
            // Table reactiveness
            rateDateMode: false,
            availabilityDateMode: false,
        
            // Editing states
            selectedRoomType: null,
            openEditAvailabilityDates: false,
            openEditRateDates: false,
            openEditRateMisc: false,
            openEditUniversalRates: false,
            
            showEditForm: false,
            selectedRateColumn: 'rate_standard',
            selectedRateDates : [],
            selectedAvailabilityDates : [],
            editingRateDates: [],
            editingAvailabilityDates: [],
            
            isSaving: false,
            saveSuccess: false,
            saveError: false,
            errorMessage: '',
        }
    },

    methods: {
        /**
         * @see data allFacilities
         */
        loadAllData(){
            let fromString = this.dateFrom.getFullYear() + '-' + (this.dateFrom.getMonth() +  1) + '-' + this.dateFrom.getDate();

            axios.get('/api/hotel-planner/table', {'params': { 'hotelKey': this.hotelkey, 'dateFrom': fromString} } )
                .then(function(response){
                    this.localResponseData = response.data;
                    this.localTableData = JSON.parse(JSON.stringify(response.data.tableData));
                }
                .bind(this));
            },

        undoChanges(){
          this.localTableData = JSON.parse(JSON.stringify(this.localResponseData.tableData));  
        },

        getDayOfWeek(index){
            switch(index){
                case 0: return 'Sun';
                case 1: return 'Mon';
                case 2: return 'Tue';
                case 3: return 'Wed';
                case 4: return 'Thur';
                case 5: return 'Fri';
                case 6: return 'Sat';
            }
            return '?';
        },

        dateIsOutOfRange(date){
            date = new Date(date);
            return date < this.currentDate || date > this.dateUntil;
        },

        getMonthOfYear(index){
            switch(index){
                case 0: return 'Jan';
                case 1: return 'Feb';
                case 2: return 'Mar';
                case 3: return 'Apr';
                case 4: return 'May';
                case 5: return 'Jun';
                case 6: return 'Jul';
                case 7: return 'Aug';
                case 8: return 'Sep';
                case 9: return 'Oct';
                case 10: return 'Nov';
                case 11: return 'Dec';
            }

            return '?';
        },

        saveData(){
            this.saveSuccess = false;
            this.saveError = false;
            this.isSaving = true;
                        
            axios.post('/api/hotel-planner', this.savePayload)
            .then((response) => {
                if (response.data.success){
                    this.loadAllData();
                    this.isSaving = false;
                    this.showSuccessMessage();
                    this.saveError = false;
                } else {
                    this.isSaving = false;
                    this.showErrorMessage(response.data.error);
                    this.saveSuccess = false;    
                }
            }).catch((error) => {
                this.isSaving = false;
                this.showErrorMessage(error.response.data.message);
                this.saveSuccess = false;
                console.log(error);
            })
        },

        showSuccessMessage(){
            this.saveSuccess = true;
            setTimeout(() => {
                this.saveSuccess = false;
            }, 5000);
        },

        showErrorMessage(errorMessage){
            this.saveError = true;
            this.errorMessage = errorMessage;
            
            setTimeout(() => {
                this.saveError = false;
                this.errorMessage = '';
            }, 5000);
        },

        handleKeyup(event){
            if (event.key === "Escape") {
                this.unselectDates();
            }
        },

        handleSelectAvailabilityDate(date){
            if (this.availabilityDateMode){
                this.selectedAvailabilityDates.push(date);
            }
        },

        handleSelectRateDate(date){
            if (this.rateDateMode){
                this.selectedRateDates.push(date);
            }
        },

        handleOpenEditAvailabilityDates(){
            this.openEditAvailabilityDates = true;
            this.selectedRateDates = [];
            this.showEditForm = true;
        },

        handleOpenEditRateDates(){
            this.openEditRateDates = true;
            this.selectedAvailabilityDates = [];
            this.showEditForm = true;
        },

        handleOpenEditUniversalRates(){
            this.openEditUniversalRates = true;
            this.selectedRateDates = [];
            this.selectedAvailabilityDates = [];
            this.showEditForm = true;
        },

        handleCloseEditForm(){
            this.showEditForm = false;
            this.openEditRateDates = false;
            this.openEditAvailabilityDates = false;
            this.openEditUniversalRates = false;
        },

        unselectDates(){
            this.selectedRateDates = [];
            this.selectedAvailabilityDates = [];
            this.selectedRateColumn = null;
        },

        handleBeforeUnload(event) {
            if (JSON.stringify(this.localTableData) != JSON.stringify(this.localResponseData.tableData)) {
                event.preventDefault();
                event.returnValue = "";
            }
        },

    },

    /**
     * Once mounted this component needs to load all available models
     */
    mounted() {
        this.loadAllData();
        document.addEventListener('keyup', this.handleKeyup);
        document.addEventListener('mouseup', () => {
            this.availabilityDateMode = false;
            this.rateDateMode = false;
        });

        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },

    computed: {
        savePayload: function(){
            // local table data with from and to dates
            let payload = JSON.parse(JSON.stringify(this.localTableData));
            payload.hotelKey = this.hotelkey;
            payload.dateFrom = this.dateFrom;
            payload.dateUntil = this.dateUntil;

            return payload; 
        },

        orderedHotelRooms: function(){
            if (!this.localTableData.hotelRooms) {
                return [];
            }

            // convert to array
            let hotelRooms = Object.values(this.localTableData.hotelRooms);
            
            return hotelRooms.sort((a, b) => (a.minRate > b.minRate) ? 1 : -1);
        },

        dateUntil: function(){
            let dateUntil = new Date(this.dateFrom.getFullYear(), this.dateFrom.getMonth() + 1);
            dateUntil.setDate(dateUntil.getDate() - 1);
            dateUntil.setHours(23, 59, 59);

            return dateUntil;
        },

        calendarDates: function(){
            var calendarDates = [];

            for(var dayIndex = 0; dayIndex <= 30; dayIndex++){
                var myDate = new Date( this.dateFrom );
                myDate.setDate(myDate.getDate() + dayIndex);
                var day = {
                    date: myDate,
                    dayIndex: myDate.getDay(),
                    dayString: this.getDayOfWeek( myDate.getDay() ),
                    dateOfMonth: myDate.getDate(),
                    monthString: this.getMonthOfYear( myDate.getMonth() ),
                    };
                calendarDates.push(day);
            }

            return calendarDates;
        },

        datesWithinRange: function(){ 
            return this.calendarDates.filter(day => day.date >= this.currentDate && day.date <= this.dateUntil).map(day => day.date);
        },

        hasUniversalRates: function(){
            return this.localTableData.universalRates ? true : false;
        },

        rateExtraAdultUniversal: function(){
            return this.localTableData.universalRates?.extra_adult_rate_amount;
        },

        rateExtraChildUniversal: function(){
            return this.localTableData.universalRates?.extra_child_rate_amount;
        },

        rateExtraBabyUniversal: function(){
            return this.localTableData.universalRates?.extra_baby_rate_amount;
        },

        rateExtraOldchildUniversal: function(){
            return this.localTableData.universalRates?.extra_oldchild_rate_amount;
        },

        showUniversalRates: function(){
            return this.hasUniversalRates && (
                this.selectedTableViewOptions.showExtraBedRate ||
                this.selectedTableViewOptions.showExtraChildBedRate ||
                this.selectedTableViewOptions.showExtraOldChildBedRate ||
                this.selectedTableViewOptions.showExtraCotRate
            );
        },

        extraBabyText: function(){
            let rateOptions = this.tableViewOptions?.rateViewOptions;

            if (rateOptions){
                let extraBabyText = rateOptions.find(rateOption => rateOption.field == 'rate_extra_baby');

                return extraBabyText?.text;
            }
        },

        extraChildText: function(){
            let rateOptions = this.tableViewOptions?.rateViewOptions;
            
            if (rateOptions){
                let extraChildText = rateOptions.find(rateOption => rateOption.field == 'rate_extra_child');
                
                return extraChildText?.text;
            }
        },

        extraOldChildText: function(){
            let rateOptions = this.tableViewOptions?.rateViewOptions;
            
            if (rateOptions){
                let extraOldChildText = rateOptions.find(rateOption => rateOption.field == 'rate_extra_oldchild');
                
                return extraOldChildText?.text;
            }
        },

        extraAdultText: function(){
            let rateOptions = this.tableViewOptions?.rateViewOptions;
            
            if (rateOptions){
                let extraAdultText = rateOptions.find(rateOption => rateOption.field == 'rate_extra_adult');
                
                return extraAdultText?.text;
            }
        },

          lastUpdated: function(){
            return this.localResponseData.lastUpdated || { date: '0000-00-00', user: '' };
        },

        selectedRoomTypeKeys: function(){
            this.selectedTableViewOptions.roomTypeOptions.forEach(element => {
                console.log(element);
            });
            return this.selectedTableViewOptions.roomTypeOptions;
        },

        selectedRateTypeKeys: function(){
            return this.selectedTableViewOptions.rateTypeOptions;
        },

        responseDataReady: function(){
            return Object.keys(this.localResponseData).length > 0;
        },

        tableCellWidthPx: function(){
	    let maxRateCharacters = this.localResponseData.maxRate ? this.localResponseData.maxRate.toString().length : 2;
            return maxRateCharacters * 8;
        }
    },

    watch: 
        {
        dateFrom: function(){
            this.loadAllData();
        },

        availabilityDateMode: function(){
            if (this.availabilityDateMode){
                this.selectedRateDates = [];
            }
        },

        rateDateMode: function(){
            if (this.rateDateMode){
                this.selectedAvailabilityDates = [];
            }
        },

    }
}
</script>

<style>


</style>
